import { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import Lottie from 'lottie-react';
import magicCupLottie from '../../../../assets/lottie_animations/magic_cup_loader.json';
import { OrderAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/OrderAPIHandler';
import { Order, OrderStatus } from '../../../../Models/Order';
import WorkspaceOrderCard from './WorkspaceOrderCard';
import GGModalWindow from '../../../UtilityComponents/GGModalWindow/GGModalWindow';
import GGDialogueBox, { dialogueType } from '../../../UtilityComponents/GGDialogueBox/GGDialogueBox';
import { TextField } from '@mui/material';
import GradingOutlinedIcon from '@mui/icons-material/GradingOutlined';
import GGButton from '../../../CommonComponents/GGButton/GGButton';
import UploadImagesSectionDraggable, { UploadableImage } from '../../../UtilityComponents/UploadImagesSection/UploadImagesSection';
import imageCompression from 'browser-image-compression';
import { addWatermarkToImage } from '../../../../utils/WatermarkUtil';
import { ProgressReviewAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/ProgressReviewAPIHandler';
import { ResumableUploadTask } from '../../../../services/UploadClient/ResumableUploadTask';
import { UploadPurpose } from '../../../../services/NetworkClient/BackendAPIs/UploadAPIHandler';
import CustomMessageDeliveryHandler from '../../../../services/ChatManager/CustomMessageDeliveryHandler';
import { useNavigate } from 'react-router-dom';
import GGLoaderButton from '../../../CommonComponents/GGLoaderButton/GGLoaderButton';
import SubmitFinalWorkPage1 from '../../../../pages/SellerWorkSpacePage/SubmitFinalWorkPage1';
import SubmitFinalWorkPage2 from '../../../../pages/SellerWorkSpacePage/SubmitFinalWorkPage2';
import logAnalyticsEvent from '../../../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../../../services/AnalyticsManager/GGAnalyticsEvents';
import '../../../../sass/global.scss';
import './WorkspacePanel.scss';
import { ErrorLogger } from '../../../../utils/Logger';

export const WorkspacePanel = () => {
    const navigate = useNavigate();
    const [listOfOrders, setListOfOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

    // Share Progress Review Modal State
    const [openShareProgressReviewModal, setOpenShareProgressReviewModal] = useState<boolean>(false);
    const [progressReviewImagesToUpload, setProgressReviewImagesToUpload] = useState<UploadableImage[]>([]);
    const [progressReviewUploadProgress, setProgressReviewUploadProgress] = useState<number>(0);
    const [progressReviewUploading, setProgressReviewUploading] = useState<boolean>(false);
    const [progressReviewError, setProgressReviewError] = useState<string | null>(null);
    const [showProgressReviewErrorDialog, setShowProgressReviewErrorDialog] = useState<boolean>(false);
    const [showProgressReviewConfirmationDialog, setShowProgressReviewConfirmationDialog] = useState<boolean>(false);
    const [progressReviewRemarks, setProgressReviewRemarks] = useState<string>('');
    const progressReviewTasks = useRef<ResumableUploadTask[]>([]);
    const [progressReviewAbortController, setProgressReviewAbortController] = useState<AbortController | null>(null);
    const [showProgressReviewAbortDialog, setShowProgressReviewAbortDialog] = useState(false);

    // Submit Final Work Modal State
    const [openSubmitFinalWorkModal, setOpenSubmitFinalWorkModal] = useState<boolean>(false);
    const [currentModalPage, setCurrentModalPage] = useState<number>(1);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const [previewImagesToUpload, setPreviewImagesToUpload] = useState<UploadableImage[]>([]);
    const [remarks, setRemarks] = useState<string>('');

    useEffect(() => {
        document.title = 'GenieGig | Workspace';
        logAnalyticsEvent(GGAnalyticsEvents.SELLER_DASHBOARD_PAGE_LOADED);
        getOrdersBySellerId();
    }, []);

    const getOrdersBySellerId = async () => {
        try {
            setLoading(true);
            const orders = await OrderAPIHandler.shared.getOrdersBySellerId();
            if (orders) {
                setListOfOrders(orders);
            }
        } catch (error) {
            ErrorLogger.log(error, 'WorkspacePanel.getOrdersBySellerId');
        } finally {
            setLoading(false);
        }
    };

    const shareProgressReview = (order: Order) => {
        setSelectedOrder(order);
        setOpenShareProgressReviewModal(true);
    };

    const closeProgressReviewModal = () => {
        setOpenShareProgressReviewModal(false);
        setSelectedOrder(null);
    };

    const submitFinalWork = (order: Order) => {
        setOpenSubmitFinalWorkModal(true);
        setCurrentModalPage(1);
        setSelectedOrder(order);
    };

    const closeSubmitFinalWorkModal = () => {
        setOpenSubmitFinalWorkModal(false);
        setSelectedOrder(null);
    };

    const shareProgressReviewBtnHandler = () => {
        setShowProgressReviewConfirmationDialog(false);
        handleShareProgressReview();
    };

    const handleShareProgressReview = async () => {
        if (progressReviewImagesToUpload.length === 0) {
            setProgressReviewError('Please upload at least one image.');
            setShowProgressReviewErrorDialog(true);
            return;
        }
        if (!progressReviewRemarks.trim()) {
            setProgressReviewError('Remarks are required.');
            setShowProgressReviewErrorDialog(true);
            return;
        }

        setProgressReviewUploading(true);
        setProgressReviewUploadProgress(0);

        const abortController = new AbortController();
        setProgressReviewAbortController(abortController);

        try {
            const files: File[] = progressReviewImagesToUpload
                .map((uploadableFile) => uploadableFile.file)
                .filter((file): file is File => file !== undefined);

            const compressedFiles: File[] = await Promise.all(
                files.map(async (file) => {
                    const options = {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1920,
                        useWebWorker: true,
                        fileType: 'image/jpeg',
                        initialQuality: 0.5,
                    };
                    const compressedFile = await imageCompression(file, options);
                    const watermarkedBlob = await addWatermarkToImage(
                        compressedFile,
                        'GenieGig © 2025 (PlatinumSpark Technologies Pvt Ltd)'
                    );
                    const jpegFileName = file.name.replace(/\.[^/.]+$/, '.jpg');
                    return new File([watermarkedBlob], jpegFileName, { type: 'image/jpeg' });
                })
            );

            const percentages = new Array(compressedFiles.length).fill(0);
            const assets = await Promise.all(compressedFiles.map((file, index) => {
                const task = new ResumableUploadTask(file, {
                    purpose: UploadPurpose.PROGRESS_REVIEW_ATTACHMENT,
                    resourceId: selectedOrder?.id,
                    onProgress: (progress) => {
                        percentages[index] = progress.percentage;
                        setProgressReviewUploadProgress(percentages.reduce((sum, value) => sum + value, 0) / percentages.length);
                    },
                });
                progressReviewTasks.current.push(task);
                return task.start();
            }));
            const assetIds = assets.map((asset) => asset.assetId);

            const progressReviewData = {
                order: selectedOrder?.id,
                remarks: progressReviewRemarks,
                screenshotAssetIds: assetIds,
            };

            const progressReview = await ProgressReviewAPIHandler.shared.postReview(progressReviewData);
            const channelId = selectedOrder?.metaData.channelId ?? '';
            const customMessageDeliveryHandler = new CustomMessageDeliveryHandler(channelId);
            await customMessageDeliveryHandler.sendProgressReviewCustomMessage(progressReview);

            setProgressReviewUploading(false);
            setProgressReviewUploadProgress(0);
            setProgressReviewImagesToUpload([]);
            setProgressReviewRemarks('');
            setProgressReviewAbortController(null);
            closeProgressReviewModal();

            navigate('/freelancer-dashboard/messages', { state: { channelId } });
        } catch (error: any) {
            setProgressReviewUploading(false);
            setProgressReviewUploadProgress(0);
            setProgressReviewAbortController(null);

            setProgressReviewError(error?.message || 'Something went wrong. Please try again.');
            setShowProgressReviewErrorDialog(true);
        }
    };

    const abortProgressReviewUpload = async () => {
        if (progressReviewAbortController) {
            progressReviewAbortController.abort();
        }
        setProgressReviewUploading(false);
        setProgressReviewUploadProgress(0);
        setProgressReviewAbortController(null);
        setShowProgressReviewAbortDialog(false);

        progressReviewTasks.current.forEach((task) => { task.cancel().catch(() => {}); });
    };

    const nextPageHandler = () => {
        setCurrentModalPage((currentModalPage) => currentModalPage + 1);
    };

    const prevPageHandler = () => {
        setCurrentModalPage((currentModalPage) => currentModalPage - 1);
    };

    const ShareProgressModalContent = !progressReviewUploading ? (
        <div className="share-progress-review-modal-content">
            <Typography className="modal-content-item">Upload screenshots of work for review</Typography>
            <br />
            <UploadImagesSectionDraggable
                editMode={true}
                maxLimit={100}
                images={progressReviewImagesToUpload}
                setImages={setProgressReviewImagesToUpload}
            />
            <br />
            <Typography className="modal-content-item">Remarks*</Typography>
            <TextField
                id="outlined-multiline-static"
                required
                multiline
                rows={7}
                value={progressReviewRemarks}
                onChange={(e) => setProgressReviewRemarks(e.target.value)}
            />
            <GGButton
                className="share-PR-btn"
                onClick={() => setShowProgressReviewConfirmationDialog(true)}
                variant="contained"
                startIcon={<GradingOutlinedIcon />}
            >
                Share Progress Review
            </GGButton>
            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={showProgressReviewConfirmationDialog}
                message="Are you sure you want to share this progress review? This action cannot be undone."
                closeHandler={() => setShowProgressReviewConfirmationDialog(false)}
                successHandler={() => {
                    shareProgressReviewBtnHandler();
                }}
            />
        </div>
    ) : (
        <div style={{ margin: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography>Uploading... {progressReviewUploadProgress}%</Typography>
            <progress value={progressReviewUploadProgress} max={100} style={{ width: '100%' }} />
            <GGLoaderButton
                color="error"
                circularProgressColorHex="#BCBCBC"
                variant="outlined"
                style={{ margin: '10px', backgroundColor: 'white', color: 'red' }}
                disabled={progressReviewUploadProgress === 100}
                isLoading={progressReviewUploadProgress === 100}
                onClick={() => setShowProgressReviewAbortDialog(true)}
            >
                Abort Upload
            </GGLoaderButton>
            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={showProgressReviewAbortDialog}
                message="Are you sure you want to abort the upload? All progress will be lost."
                closeHandler={() => setShowProgressReviewAbortDialog(false)}
                successHandler={abortProgressReviewUpload}
            />
        </div>
    );

    const SubmitFinalWorkModalContent =
        currentModalPage === 1 ? (
            <SubmitFinalWorkPage1
                filesToUpload={filesToUpload}
                setFilesToUpload={setFilesToUpload}
                nextPage={nextPageHandler}
                order={selectedOrder}
                previewImagesToUpload={previewImagesToUpload}
                setPreviewImagesToUpload={setPreviewImagesToUpload}
                remarks={remarks}
                setRemarks={setRemarks}
            />
        ) : (
            <SubmitFinalWorkPage2
                previewImagesToUpload={previewImagesToUpload}
                filesToUpload={filesToUpload}
                prevPage={prevPageHandler}
                order={selectedOrder}
                remarks={remarks}
                onDeliverySuccess={getOrdersBySellerId}
                closeModal={closeSubmitFinalWorkModal}
            />
        );

    if (loading) {
        return (
            <Box className="workspace-loading-container">
                <Lottie className="magic_cup_lottie" animationData={magicCupLottie} loop={true} autoplay={true} />
            </Box>
        );
    }

    const activeOrders = listOfOrders.filter(
        (order) => order.status !== OrderStatus.completed && order.status !== OrderStatus.cancelled
    );

    return (
        <Box className="workspace-panel">
            <GGDialogueBox
                type={dialogueType.Error}
                isOpen={showProgressReviewErrorDialog}
                message={progressReviewError || ''}
                closeHandler={() => setShowProgressReviewErrorDialog(false)}
                successHandler={() => {
                    setShowProgressReviewErrorDialog(false);
                }}
            />

            <GGModalWindow
                childView={ShareProgressModalContent}
                open={openShareProgressReviewModal}
                title="Share Progress Review"
                onCloseHandler={closeProgressReviewModal}
            />

            <GGModalWindow
                width="70vw"
                backgroundColor={currentModalPage === 2 ? '#F3F3F3' : 'white'}
                childView={SubmitFinalWorkModalContent}
                disableCloseButton={currentModalPage === 2}
                open={openSubmitFinalWorkModal}
                title="Submit Final Work"
                onCloseHandler={closeSubmitFinalWorkModal}
            />

            <div className="workspace-orders-container">
                {activeOrders.length === 0 ? (
                    <div className="no-orders-container">
                        <Typography variant="h5" className="no-orders-text">
                            No orders available yet
                        </Typography>
                        <Typography variant="body1" className="no-orders-helper-text">
                            The workspace tab is the place to manage all the ongoing orders/projects parallelly and
                            efficiently. Once the order is completed, they are moved to my orders tab.
                        </Typography>
                    </div>
                ) : (
                    activeOrders.map((order) => (
                        <WorkspaceOrderCard
                            key={order.id}
                            initialOrder={order}
                            onSubmitFinalWork={submitFinalWork}
                            onShareProgressReview={shareProgressReview}
                        />
                    ))
                )}
            </div>
        </Box>
    );
};

